// SCR-STAFF-101 — ثبت سریع (FLOW-01؛ US-PAT-001، 030، 063): ترتیب فیلد با موبایل اول، مشابه‌های زنده پس از موبایل یا نام
// (کد ملی ← بلوک و «باز کن»؛ احتمالی ← انتخاب یا «فرد متفاوت است»؛ موبایل مشترک ← هشدار)، کاملاً با کیبورد: Tab بین فیلدها،
// Enter ← «ثبت و ادامه به نوبت»، Esc ← انصراف؛ ارقام فارسی و عربی خودکار نرمال می‌شوند (AC-PAT-001.4).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type DuplicateCheckResult,
  type PatientSearchItem,
  type PatientView,
  type RegisterPatientRequest,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  ErrorState,
  isValidMobile,
  isValidNationalId,
  keepDigitsOnly,
  latinDigits,
  MobileFieldRHF,
  NationalIdFieldRHF,
  normalizeMobile,
  normalizePersian,
  TextFieldRHF,
  zMobile,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { parseJalaliInput, PatientBadgeChips, patKeys, useDebounced, useReferralSources } from './common';
import { PatientResultSummary, usePatientSearch } from './PatientSearchPalette';

const schema = z
  .object({
    mobile: zMobile(),
    firstName: zRequiredText(100),
    lastName: zRequiredText(100),
    gender: z.enum(['Female', 'Male'], { error: 'common:validation.required' }),
    nationalId: z.string(),
    birthDate: z.string(),
    referralSourceId: z.string(),
    referrerPatientId: z.string(),
    confirmDistinct: z.boolean(),
  })
  .superRefine((value, context) => {
    if (value.nationalId && !isValidNationalId(value.nationalId)) {
      context.addIssue({ code: 'custom', path: ['nationalId'], message: 'common:validation.national_id' });
    }
    if (value.birthDate.trim() && !parseJalaliInput(value.birthDate)) {
      context.addIssue({ code: 'custom', path: ['birthDate'], message: 'pat:validation.birth_date' });
    }
  });

type RegisterForm = z.input<typeof schema>;
const FIELDS = ['mobile', 'firstName', 'lastName', 'gender', 'nationalId', 'birthDate', 'referralSourceId'] as const;

export type RegisterCompletion = 'continue' | 'only';

/** پیش‌پر کردن از ورودی جستجو: موبایل، کد ملی یا نام و نام خانوادگی. */
export function prefillFromSearch(text: string | undefined): Partial<RegisterForm> {
  const value = latinDigits(text ?? '').trim();
  if (!value) {
    return {};
  }
  const digits = keepDigitsOnly(value);
  if (/^[\d\s-]+$/.test(value)) {
    if (isValidMobile(digits)) {
      return { mobile: normalizeMobile(digits) ?? digits };
    }
    if (digits.length === 10) {
      return { nationalId: digits };
    }
    return {};
  }
  const [first, ...rest] = normalizePersian(value).split(' ');
  return { firstName: first ?? '', lastName: rest.join(' ') };
}

function emptyForm(prefill: Partial<RegisterForm>): RegisterForm {
  return {
    mobile: '',
    firstName: '',
    lastName: '',
    gender: 'Female',
    nationalId: '',
    birthDate: '',
    referralSourceId: '',
    referrerPatientId: '',
    confirmDistinct: false,
    ...prefill,
  };
}

/** بررسی تکراری زنده (US-PAT-030) پس از موبایل یا نام یا کد ملی معتبر. */
function useLiveDuplicates(values: Partial<RegisterForm>, enabled: boolean) {
  const { tenantApi, session } = useTenantSession();
  const mobile = normalizeMobile(values.mobile ?? '') ?? null;
  const nationalId = values.nationalId && isValidNationalId(values.nationalId) ? values.nationalId : null;
  const firstName = normalizePersian(values.firstName ?? '');
  const lastName = normalizePersian(values.lastName ?? '');
  const birthDate = parseJalaliInput(values.birthDate) ?? null;
  const request = useDebounced(
    JSON.stringify({ mobile, nationalId, firstName, lastName, birthDate }),
    400,
  );
  const parsed = JSON.parse(request) as {
    mobile: string | null;
    nationalId: string | null;
    firstName: string;
    lastName: string;
    birthDate: string | null;
  };
  const ready = Boolean(parsed.mobile || parsed.nationalId || (parsed.firstName && parsed.lastName));
  return useQuery({
    queryKey: patKeys.duplicates(session?.tenantId, request),
    queryFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients:check-duplicates', {
          body: { ...parsed, excludePatientId: null },
        }),
      ),
    enabled: enabled && ready,
    staleTime: 5_000,
  });
}

function DuplicatePanel({
  result,
  checking,
  onOpen,
}: {
  result: DuplicateCheckResult | undefined;
  checking: boolean;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation('pat');
  const blocking = result?.blocking;
  const probable = result?.probable ?? [];
  const shared = result?.sharedMobile ?? [];
  const any = Boolean(blocking) || probable.length > 0 || shared.length > 0;
  if (!checking && !any) {
    return null;
  }
  const row = (item: PatientSearchItem, label: string, testId: string, severity: 'error' | 'warning' | 'info') => (
    <Alert
      key={`${testId}-${item.id}`}
      severity={severity}
      data-testid={testId}
      action={
        <Button color="inherit" size="small" onClick={() => onOpen(item.id)} data-testid={`${testId}-open`}>
          {t('pat:duplicates.open')}
        </Button>
      }
    >
      <Stack spacing={0.25}>
        <Typography variant="body2" component="span">
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" component="span">
          <strong>{item.displayName}</strong>
          <PatientBadgeChips badges={item.badges} />
        </Stack>
        <Typography variant="caption" component="span">
          <PatientResultSummary item={item} />
        </Typography>
      </Stack>
    </Alert>
  );
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} aria-live="polite" data-testid="duplicate-panel">
      <Typography variant="subtitle2" gutterBottom>
        {t('pat:duplicates.title')}
      </Typography>
      {checking && !any ? <LinearProgress aria-label={t('pat:duplicates.checking')} /> : null}
      <Stack spacing={1}>
        {blocking && row(blocking, t('pat:duplicates.blocking'), 'duplicate-blocking', 'error')}
        {probable.map((candidate) =>
          row(candidate.patient, t('pat:duplicates.probable', { score: candidate.score }), 'duplicate-probable', 'warning'),
        )}
        {shared.map((item) =>
          row(item, `${t('pat:duplicates.shared_mobile', { name: item.displayName })} — ${t('pat:duplicates.shared_mobile_hint')}`, 'duplicate-shared', 'info'),
        )}
      </Stack>
    </Paper>
  );
}

/** انتخاب مراجعه‌کننده معرف (US-PAT-063) با جستجوی سرور. */
function ReferrerPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation('pat');
  const [text, setText] = useState('');
  const search = usePatientSearch(text, 10);
  const options = search.data?.items ?? [];
  return (
    <Autocomplete<PatientSearchItem>
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={options.find((option) => option.id === value) ?? null}
      onChange={(_, option) => onChange(option?.id ?? '')}
      inputValue={text}
      onInputChange={(_, next) => setText(next)}
      loading={search.isFetching}
      renderInput={(params) => (
        <TextField {...params} label={t('pat:fields.referrer')} helperText={t('pat:fields.referrer_help')} />
      )}
    />
  );
}

/** مقدار آغازین فیلدهای ثبت سریع (مثلاً تبدیل سرنخ ENG — AC-ENG-005.1). */
export type PatientRegisterInitial = Partial<Pick<RegisterForm, 'mobile' | 'firstName' | 'lastName' | 'gender' | 'referralSourceId'>>;

export interface PatientRegisterDialogProps {
  open: boolean;
  /** متن جستجو برای پیش‌پر کردن (ثبت جدید از نتیجه خالی). */
  prefill?: string;
  /** پیش‌پر کردن صریح فیلدها (پس از prefill؛ مرجع پایدار بدهید). */
  initial?: PatientRegisterInitial;
  onClose: () => void;
  onRegistered: (patient: PatientView, completion: RegisterCompletion) => void;
  onOpenPatient: (id: string) => void;
}

export function PatientRegisterDialog({ open, prefill, initial, onClose, onRegistered, onOpenPatient }: PatientRegisterDialogProps) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const titleId = useId();
  const genderId = useId();
  const [completion, setCompletion] = useState<RegisterCompletion>('continue');
  const [submitError, setSubmitError] = useState<unknown>();
  const [blockedBy, setBlockedBy] = useState<PatientSearchItem | null>(null);
  const sources = useReferralSources();

  const defaults = useMemo(() => emptyForm({ ...prefillFromSearch(prefill), ...initial }), [prefill, initial]);
  const { control, handleSubmit, reset, setError, clearErrors, getValues } = useForm<RegisterForm>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });
  useEffect(() => {
    if (open) {
      reset(defaults);
      setSubmitError(undefined);
      setBlockedBy(null);
    }
  }, [open, defaults, reset]);

  const watched = useWatch({ control });
  const duplicates = useLiveDuplicates(watched, open);
  const blocking = duplicates.data?.blocking ?? blockedBy;
  const probable = duplicates.data?.probable ?? [];
  const selectedSource = sources.data?.find((source) => source.id === watched.referralSourceId);

  const register = useMutation({
    mutationFn: (body: RegisterPatientRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (patient) => {
      void queryClient.invalidateQueries({ queryKey: patKeys.all(session?.tenantId) });
      onRegistered(patient, completion);
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'PHX-PAT-0301') {
        const id = error.extensions['patientId'];
        const fileNumber = error.extensions['fileNumber'];
        if (typeof id === 'string') {
          setBlockedBy({
            id,
            displayName: `${getValues('firstName')} ${getValues('lastName')}`,
            fileNumber: typeof fileNumber === 'string' ? fileNumber : '',
            previousFileNumber: null,
            gender: getValues('gender'),
            age: null,
            mobileHint: null,
            lastVisitAt: null,
            badges: { minor: false, vip: false, incomplete: false, deceased: false },
          });
        }
      }
      if (isApiError(error) && error.code === 'PHX-PAT-0302') {
        setError('confirmDistinct', { type: 'server', message: 'pat:validation.confirm_distinct' }, { shouldFocus: true });
        void duplicates.refetch();
        return;
      }
      const applied = applyServerErrors(error, setError, {
        fields: FIELDS,
        codeFields: { 'PHX-PAT-0101': 'nationalId', 'PHX-PAT-0301': 'nationalId', 'PHX-PAT-0102': 'birthDate', 'PHX-PAT-0602': 'referralSourceId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = (mode: RegisterCompletion) =>
    handleSubmit((form) => {
      setCompletion(mode);
      if (blocking) {
        setError('nationalId', { type: 'server', message: 'errors:error.patient_national_id_duplicate' }, { shouldFocus: true });
        return;
      }
      if (probable.length > 0 && !form.confirmDistinct) {
        setError('confirmDistinct', { type: 'manual', message: 'pat:validation.confirm_distinct' }, { shouldFocus: true });
        return;
      }
      clearErrors('confirmDistinct');
      register.mutate({
        mobile: form.mobile,
        firstName: form.firstName,
        lastName: form.lastName,
        gender: form.gender,
        nationalId: form.nationalId || null,
        birthDate: parseJalaliInput(form.birthDate) ?? null,
        referralSourceId: form.referralSourceId || null,
        referrerPatientId: selectedSource?.allowsReferrer && form.referrerPatientId ? form.referrerPatientId : null,
        confirmDistinct: form.confirmDistinct,
      });
    });

  const close = () => {
    if (!register.isPending) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
      data-screen-id="SCR-STAFF-101"
    >
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit('continue')();
        }}
      >
        <DialogTitle id={titleId}>{t('pat:register.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <MobileFieldRHF control={control} name="mobile" labelKey="pat:fields.mobile" required autoFocus fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextFieldRHF control={control} name="firstName" labelKey="pat:fields.first_name" required fullWidth />
              <TextFieldRHF control={control} name="lastName" labelKey="pat:fields.last_name" required fullWidth />
            </Stack>
            <Controller
              control={control}
              name="gender"
              render={({ field, fieldState }) => (
                <FormControl error={Boolean(fieldState.error)} required>
                  <FormLabel id={genderId}>{t('pat:fields.gender')}</FormLabel>
                  <RadioGroup row aria-labelledby={genderId} name={field.name} value={field.value} onChange={field.onChange}>
                    <FormControlLabel value="Female" control={<Radio inputRef={field.ref} />} label={t('pat:gender.Female')} />
                    <FormControlLabel value="Male" control={<Radio />} label={t('pat:gender.Male')} />
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <NationalIdFieldRHF control={control} name="nationalId" labelKey="pat:fields.national_id" fullWidth />
              <TextFieldRHF
                control={control}
                name="birthDate"
                labelKey="pat:fields.birth_date"
                helperKey="pat:fields.birth_date_help"
                fullWidth
                slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'register-birth-date' } }}
              />
            </Stack>
            <Controller
              control={control}
              name="referralSourceId"
              render={({ field, fieldState }) => (
                <TextField
                  select
                  fullWidth
                  label={t('pat:fields.referral_source')}
                  name={field.name}
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                >
                  <option value="">{t('pat:fields.none')}</option>
                  {(sources.data ?? []).map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </TextField>
              )}
            />
            {selectedSource?.allowsReferrer && (
              <Controller
                control={control}
                name="referrerPatientId"
                render={({ field }) => <ReferrerPicker value={field.value} onChange={field.onChange} />}
              />
            )}
            <DuplicatePanel
              result={blockedBy ? { blocking: blockedBy, probable: [], sharedMobile: [] } : duplicates.data}
              checking={duplicates.isFetching}
              onOpen={onOpenPatient}
            />
            {probable.length > 0 && !blocking && (
              <CheckboxRHF control={control} name="confirmDistinct" labelKey="pat:duplicates.confirm_distinct" />
            )}
          </Stack>
          {submitError ? <ErrorState error={submitError} compact /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={register.isPending} data-testid="register-cancel">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() => void submit('only')()}
            disabled={register.isPending || Boolean(blocking)}
            data-testid="register-only"
          >
            {t('pat:register.submit_only')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={register.isPending || Boolean(blocking)}
            data-testid="register-submit"
          >
            {t('pat:register.submit_continue')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
